import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { SiteSettingsDto } from '@lex/contracts';
import { Button } from '@lex/ui';
import { useAuth } from '../../lib/auth';
import { CloseIcon, MenuIcon } from '../icons';
import { Emblem } from '../home/primitives';

export function SiteHeader({ settings }: { settings: SiteSettingsDto }) {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const cta = settings.primaryCta;
  const initials = (user?.name ?? user?.email ?? 'Л')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
  const navHref = (href: string): string => (href === '/#tariffs' ? '/tariffs' : href);
  const go = (href: string) => {
    setOpen(false);
    void navigate(href);
  };

  return (
    <header className="sticky top-0 z-50 px-4 pt-3 md:pt-6">
      <div className="mx-auto w-full max-w-[1280px]">
        <div className="flex h-[68px] items-center justify-between gap-4 rounded-2xl border border-white/90 bg-white/70 px-4 pl-5 shadow-nav backdrop-blur-[14px] md:h-[76px] md:pl-7">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2.5"
            onClick={() => setOpen(false)}
            aria-label="lex.ai — на главную"
          >
            <Emblem />
            <span className="text-[22px] font-bold tracking-[-0.01em] text-ink">
              lex<span className="text-accent">.ai</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-5 lg:flex xl:gap-6" aria-label="Главное меню">
            {settings.navLinks.map((link) => (
              <Link
                key={`${link.href}-${link.label}`}
                to={navHref(link.href)}
                className="whitespace-nowrap text-[14.5px] font-medium text-ink transition-colors hover:text-accent"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-5 lg:flex">
            {isAuthenticated ? (
              <>
                <Link to="/cases" className="whitespace-nowrap text-[14.5px] font-medium text-ink hover:text-accent">
                  Мои дела
                </Link>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setProfileOpen((value) => !value)}
                    className="flex items-center gap-2 rounded-xl border border-line bg-white/80 py-1.5 pl-1.5 pr-3 text-sm font-semibold text-ink hover:bg-soft"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-accent">
                      {initials || 'Л'}
                    </span>
                    <span className="max-w-[120px] truncate">{user?.name ?? user?.phone ?? 'Профиль'}</span>
                  </button>
                  {profileOpen ? (
                    <div className="absolute right-0 top-[calc(100%+8px)] w-80 rounded-2xl border border-line bg-white/95 p-2 shadow-[0_16px_40px_rgba(10,28,64,0.25)] backdrop-blur-[12px]">
                      <Link
                        to="/cabinet"
                        onClick={() => setProfileOpen(false)}
                        className="block rounded-[10px] bg-accent-soft px-3.5 py-2.5 hover:bg-accent-soft"
                      >
                        <span className="block text-[15px] font-semibold text-ink">Мои обращения</span>
                        <span className="block text-[12px] text-muted">Активные диалоги и история</span>
                      </Link>
                      <Link
                        to="/cases"
                        onClick={() => setProfileOpen(false)}
                        className="mt-0.5 block rounded-[10px] px-3.5 py-2.5 hover:bg-soft"
                      >
                        <span className="block text-[15px] font-semibold text-ink">Мои дела</span>
                        <span className="block text-[12px] text-muted">Этапы, сроки и черновики</span>
                      </Link>
                      <Link
                        to="/cabinet#subscription"
                        onClick={() => setProfileOpen(false)}
                        className="mt-0.5 block rounded-[10px] px-3.5 py-2.5 hover:bg-soft"
                      >
                        <span className="block text-[15px] font-semibold text-ink">Подписка и оплата</span>
                        <span className="block text-[12px] text-muted">Тариф и платежи</span>
                      </Link>
                      <Link
                        to="/cabinet#profile"
                        onClick={() => setProfileOpen(false)}
                        className="mt-0.5 block rounded-[10px] px-3.5 py-2.5 hover:bg-soft"
                      >
                        <span className="block text-[15px] font-semibold text-ink">
                          Профиль и уведомления
                        </span>
                        <span className="block text-[12px] text-muted">Telegram и почта</span>
                      </Link>
                      <a
                        href="mailto:help@lex.ai"
                        onClick={() => setProfileOpen(false)}
                        className="mt-0.5 block rounded-[10px] px-3.5 py-2.5 hover:bg-soft"
                      >
                        <span className="block text-[15px] font-semibold text-ink">Помощь</span>
                        <span className="block text-[12px] text-muted">Написать в поддержку</span>
                      </a>
                      <div className="my-2 h-px bg-line" />
                      <button
                        type="button"
                        onClick={() => {
                          setProfileOpen(false);
                          void logout().then(() => navigate('/'));
                        }}
                        className="block w-full rounded-[10px] px-3.5 py-2.5 text-left text-[15px] font-semibold text-muted hover:bg-soft"
                      >
                        Выйти
                      </button>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <Link to="/login" className="text-[15px] font-medium text-ink hover:text-accent">
                {settings.loginLabel}
              </Link>
            )}
            <Button
              variant="ink"
              size="lg"
              className="whitespace-nowrap rounded-xl px-6"
              onClick={() => go(cta.href)}
            >
              {cta.label}
            </Button>
          </div>

          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-white/80 text-ink lg:hidden"
            aria-label={open ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <CloseIcon className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>

        {open ? (
          <div className="mt-2 rounded-2xl border border-white/90 bg-white/95 p-4 shadow-nav backdrop-blur-[14px] lg:hidden">
            <nav className="flex flex-col" aria-label="Мобильное меню">
              {settings.navLinks.map((link) => (
                <Link
                  key={`${link.href}-${link.label}`}
                  to={navHref(link.href)}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-ink hover:bg-soft"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="mt-3 flex flex-col gap-2 border-t border-line pt-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/cases"
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-ink hover:bg-soft"
                  >
                    Мои дела
                  </Link>
                  <Link
                    to="/cabinet"
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-ink hover:bg-soft"
                  >
                    Кабинет
                  </Link>
                  <div className="flex items-center gap-2 rounded-lg px-3 py-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-accent">
                      {initials || 'Л'}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
                      {user?.name ?? user?.phone ?? 'Профиль'}
                    </span>
                    <Link
                      to="/cabinet"
                      onClick={() => setOpen(false)}
                      className="text-xs font-semibold text-accent"
                    >
                      Открыть
                    </Link>
                  </div>
                  <Button
                    variant="ghost"
                    className="justify-start"
                    onClick={() => {
                      void logout().then(() => {
                        setOpen(false);
                        void navigate('/');
                      });
                    }}
                  >
                    Выйти
                  </Button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-[15px] font-medium text-ink hover:bg-soft"
                >
                  {settings.loginLabel}
                </Link>
              )}
              <Button
                variant="ink"
                size="lg"
                className="w-full rounded-xl"
                onClick={() => go(cta.href)}
              >
                {cta.label}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}
